/*
 * Buyback form: client-side validation, photo downscaling and multipart upload
 * to an external endpoint (data-endpoint). Customer-facing texts are Slovak.
 */
(function () {
  if (customElements.get('buyback-form')) {
    return;
  }

  const MESSAGES = {
    required: 'Toto pole je povinné.',
    email: 'Zadaj platnú e-mailovú adresu.',
    phone: 'Zadaj platné telefónne číslo.',
    price: 'Zadaj cenu ako kladné číslo.',
    photosRequired: 'Pridaj aspoň jednu fotku.',
    photosProcessing: 'Počkaj, kým sa fotky spracujú.',
    tooManyFiles: 'Môžeš pridať najviac {max} fotiek.',
    fileTooBig: 'Fotka {name} je väčšia ako {max} MB.',
    totalTooBig: 'Fotky spolu presahujú {max} MB.',
    notImage: 'Súbor {name} nie je obrázok.',
    noEndpoint: 'Formulár zatiaľ nie je prepojený so systémom. Skús to prosím neskôr.',
    network: 'Odoslanie zlyhalo. Skontroluj pripojenie a skús to znova.',
    server: 'Ponuku sa nepodarilo odoslať. Skús to prosím znova.',
    fixErrors: 'Skontroluj prosím označené polia.',
    count: '{count} / {max} fotiek',
    size: '{size} / {max} MB',
    uploading: 'Odosielam… {percent} %',
    remove: 'Odstrániť fotku'
  };

  const RESIZABLE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const MB = 1024 * 1024;

  function format(template, values) {
    return template.replace(/\{(\w+)\}/g, function (match, key) {
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        return String(values[key]);
      }
      return match;
    });
  }

  function formatMb(bytes) {
    return (bytes / MB).toFixed(1).replace('.', ',');
  }

  function loadImage(file) {
    return new Promise(function (resolve, reject) {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = function () {
        resolve({ img: img, url: url });
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error('decode'));
      };
      img.src = url;
    });
  }

  // Downscales large photos to JPEG. Files the browser cannot decode (e.g. HEIC
  // in Chrome) or small enough files are sent unchanged.
  async function prepareImage(file, maxSide) {
    if (RESIZABLE_TYPES.indexOf(file.type) === -1) {
      return file;
    }

    let loaded;
    try {
      loaded = await loadImage(file);
    } catch (error) {
      return file;
    }

    const img = loaded.img;
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    const longest = Math.max(width, height);

    if (longest <= maxSide && file.size <= 1.5 * MB) {
      URL.revokeObjectURL(loaded.url);
      return file;
    }

    let scale = 1;
    if (longest > maxSide) {
      scale = maxSide / longest;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(loaded.url);

    const blob = await new Promise(function (resolve) {
      canvas.toBlob(resolve, 'image/jpeg', 0.85);
    });

    if (!blob || blob.size >= file.size) {
      return file;
    }

    const baseName = file.name.replace(/\.[^.]+$/, '');
    return new File([blob], baseName + '.jpg', { type: 'image/jpeg', lastModified: Date.now() });
  }

  class BuybackForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.fileInput = this.querySelector('[data-file-input]');
      this.dropzone = this.querySelector('[data-dropzone]');
      this.previewList = this.querySelector('[data-previews]');
      this.countEl = this.querySelector('[data-count]');
      this.sizeEl = this.querySelector('[data-size]');
      this.statusEl = this.querySelector('[data-status]');
      this.submitBtn = this.querySelector('[data-submit]');
      this.progressEl = this.querySelector('[data-progress]');
      this.progressBar = this.querySelector('[data-progress-bar]');
      this.progressText = this.querySelector('[data-progress-text]');
      this.successEl = this.querySelector('[data-success]');

      this.endpoint = (this.dataset.endpoint || '').trim();
      this.maxFiles = parseInt(this.dataset.maxFiles, 10) || 20;
      this.maxFileBytes = (parseInt(this.dataset.maxFileMb, 10) || 20) * MB;
      this.maxTotalBytes = (parseInt(this.dataset.maxTotalMb, 10) || 120) * MB;
      this.maxImageSide = parseInt(this.dataset.maxImageSide, 10) || 2000;

      this.items = [];
      this.nextId = 1;
      this.sending = false;

      this.form.elements.started_at.value = String(Date.now());

      this.fileInput.addEventListener('change', this.onFileChange.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.form.addEventListener('input', this.onInput.bind(this));
      this.previewList.addEventListener('click', this.onPreviewClick.bind(this));
      this.bindDropzone();
      this.updateMeta();
    }

    bindDropzone() {
      const zone = this.dropzone;
      const self = this;
      ['dragenter', 'dragover'].forEach(function (type) {
        zone.addEventListener(type, function (event) {
          event.preventDefault();
          zone.classList.add('is-dragover');
        });
      });
      ['dragleave', 'drop'].forEach(function (type) {
        zone.addEventListener(type, function (event) {
          event.preventDefault();
          if (type === 'dragleave' && zone.contains(event.relatedTarget)) {
            return;
          }
          zone.classList.remove('is-dragover');
        });
      });
      zone.addEventListener('drop', function (event) {
        if (event.dataTransfer && event.dataTransfer.files) {
          self.addFiles(event.dataTransfer.files);
        }
      });
    }

    onFileChange() {
      this.addFiles(this.fileInput.files);
      // Reset so the same file can be picked again after removing it.
      this.fileInput.value = '';
    }

    addFiles(fileList) {
      const files = Array.prototype.slice.call(fileList);
      const problems = [];

      for (const file of files) {
        if (this.items.length >= this.maxFiles) {
          problems.push(format(MESSAGES.tooManyFiles, { max: this.maxFiles }));
          break;
        }
        if (file.type.indexOf('image/') !== 0) {
          problems.push(format(MESSAGES.notImage, { name: file.name }));
          continue;
        }
        this.addItem(file);
      }

      if (problems.length > 0) {
        this.setFieldError('photos', problems.join(' '));
      } else {
        this.setFieldError('photos', '');
      }
      this.updateMeta();
    }

    addItem(original) {
      const item = {
        id: this.nextId,
        original: original,
        file: null,
        previewUrl: URL.createObjectURL(original),
        el: null
      };
      this.nextId += 1;
      this.items.push(item);
      this.renderPreview(item);

      const self = this;
      prepareImage(original, this.maxImageSide).then(function (prepared) {
        // The item may have been removed while it was processing.
        if (self.items.indexOf(item) === -1) {
          return;
        }
        item.file = prepared;
        item.el.classList.remove('is-processing');
        if (prepared.size > self.maxFileBytes) {
          self.removeItem(item.id);
          self.setFieldError('photos', format(MESSAGES.fileTooBig, { name: original.name, max: self.maxFileBytes / MB }));
          return;
        }
        self.updateMeta();
      });
    }

    renderPreview(item) {
      const li = document.createElement('li');
      li.className = 'buyback__preview is-processing';
      li.dataset.id = String(item.id);

      const img = document.createElement('img');
      img.src = item.previewUrl;
      img.alt = '';
      img.loading = 'lazy';
      img.onerror = function () {
        img.remove();
      };

      const name = document.createElement('span');
      name.className = 'buyback__preview-name';
      name.textContent = item.original.name;

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'buyback__preview-remove';
      remove.setAttribute('aria-label', MESSAGES.remove + ' ' + item.original.name);
      remove.dataset.remove = String(item.id);
      remove.textContent = '×';

      li.appendChild(img);
      li.appendChild(name);
      li.appendChild(remove);
      this.previewList.appendChild(li);
      item.el = li;
    }

    onPreviewClick(event) {
      const button = event.target.closest('[data-remove]');
      if (!button || this.sending) {
        return;
      }
      this.removeItem(parseInt(button.dataset.remove, 10));
      this.setFieldError('photos', '');
      this.updateMeta();
    }

    removeItem(id) {
      const index = this.items.findIndex(function (entry) {
        return entry.id === id;
      });
      if (index === -1) {
        return;
      }
      const item = this.items[index];
      URL.revokeObjectURL(item.previewUrl);
      item.el.remove();
      this.items.splice(index, 1);
    }

    totalBytes() {
      let total = 0;
      for (const item of this.items) {
        if (item.file) {
          total += item.file.size;
        } else {
          total += item.original.size;
        }
      }
      return total;
    }

    updateMeta() {
      this.countEl.textContent = format(MESSAGES.count, { count: this.items.length, max: this.maxFiles });
      this.sizeEl.textContent = format(MESSAGES.size, {
        size: formatMb(this.totalBytes()),
        max: this.maxTotalBytes / MB
      });
    }

    onInput(event) {
      const name = event.target.name;
      if (name && event.target.getAttribute('aria-invalid') === 'true') {
        this.setFieldError(name, '');
      }
    }

    setFieldError(name, message) {
      const errorEl = this.querySelector('[data-error-for="' + name + '"]');
      let target = null;
      if (name === 'photos') {
        target = this.dropzone;
      } else {
        target = this.form.elements[name];
      }

      if (message) {
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
        }
        if (target) {
          if (name === 'photos') {
            target.dataset.invalid = 'true';
          } else {
            target.setAttribute('aria-invalid', 'true');
          }
        }
      } else {
        if (errorEl) {
          errorEl.textContent = '';
          errorEl.hidden = true;
        }
        if (target) {
          if (name === 'photos') {
            delete target.dataset.invalid;
          } else {
            target.removeAttribute('aria-invalid');
          }
        }
      }
    }

    validate() {
      const elements = this.form.elements;
      const errors = {};

      ['full_name', 'email', 'phone', 'description'].forEach(function (name) {
        if (!elements[name].value.trim()) {
          errors[name] = MESSAGES.required;
        }
      });

      const email = elements.email.value.trim();
      if (!errors.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.email = MESSAGES.email;
      }

      const phoneDigits = elements.phone.value.replace(/\D/g, '');
      if (!errors.phone && (phoneDigits.length < 9 || !/^[+\d\s()\/-]+$/.test(elements.phone.value.trim()))) {
        errors.phone = MESSAGES.phone;
      }

      const price = elements.price.value.trim();
      if (price !== '' && (isNaN(Number(price)) || Number(price) < 0)) {
        errors.price = MESSAGES.price;
      }

      if (this.items.length === 0) {
        errors.photos = MESSAGES.photosRequired;
      } else if (this.items.some(function (item) { return !item.file; })) {
        errors.photos = MESSAGES.photosProcessing;
      } else if (this.totalBytes() > this.maxTotalBytes) {
        errors.photos = format(MESSAGES.totalTooBig, { max: this.maxTotalBytes / MB });
      }

      const self = this;
      ['full_name', 'email', 'phone', 'description', 'price', 'photos'].forEach(function (name) {
        if (errors[name]) {
          self.setFieldError(name, errors[name]);
        } else {
          self.setFieldError(name, '');
        }
      });

      const firstInvalid = Object.keys(errors)[0];
      if (firstInvalid) {
        if (firstInvalid === 'photos') {
          this.fileInput.focus();
        } else {
          elements[firstInvalid].focus();
        }
        return false;
      }
      return true;
    }

    showStatus(message) {
      if (message) {
        this.statusEl.textContent = message;
        this.statusEl.hidden = false;
      } else {
        this.statusEl.textContent = '';
        this.statusEl.hidden = true;
      }
    }

    setSending(sending) {
      this.sending = sending;
      this.submitBtn.disabled = sending;
      this.progressEl.hidden = !sending;
      if (sending) {
        this.setProgress(0);
      }
    }

    setProgress(percent) {
      this.progressBar.style.width = percent + '%';
      this.progressText.textContent = format(MESSAGES.uploading, { percent: percent });
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.sending) {
        return;
      }
      this.showStatus('');

      if (!this.validate()) {
        this.showStatus(MESSAGES.fixErrors);
        return;
      }
      if (!this.endpoint) {
        this.showStatus(MESSAGES.noEndpoint);
        return;
      }

      const data = new FormData(this.form);
      data.set('full_name', data.get('full_name').trim());
      data.set('email', data.get('email').trim());
      data.set('phone', data.get('phone').trim());
      for (const item of this.items) {
        data.append('photos[]', item.file, item.file.name);
      }

      this.send(data);
    }

    send(data) {
      const self = this;
      const xhr = new XMLHttpRequest();
      xhr.open('POST', this.endpoint, true);
      xhr.setRequestHeader('Accept', 'application/json');

      xhr.upload.addEventListener('progress', function (event) {
        if (event.lengthComputable) {
          self.setProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)));
        }
      });

      xhr.addEventListener('load', function () {
        self.setSending(false);
        if (xhr.status >= 200 && xhr.status < 300) {
          self.showSuccess();
          return;
        }
        self.showStatus(self.serverMessage(xhr));
      });

      xhr.addEventListener('error', function () {
        self.setSending(false);
        self.showStatus(MESSAGES.network);
      });

      this.setSending(true);
      xhr.send(data);
    }

    // The backend may return {"message": "..."} with a customer-facing error.
    serverMessage(xhr) {
      try {
        const body = JSON.parse(xhr.responseText);
        if (body && typeof body.message === 'string' && body.message) {
          return body.message;
        }
      } catch (error) {
        // Not JSON, fall through to the generic message.
      }
      return MESSAGES.server;
    }

    showSuccess() {
      for (const item of this.items) {
        URL.revokeObjectURL(item.previewUrl);
      }
      this.items = [];
      this.form.hidden = true;
      this.successEl.hidden = false;
      this.successEl.focus();
      this.successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  customElements.define('buyback-form', BuybackForm);
})();
